import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User, Shield, Settings, GraduationCap } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useScrollNavigation } from "@/contexts/ScrollContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useContext } from "react";
import { ScrollContext } from "@/contexts/ScrollContext";

// Section indices mapping (must match Index.tsx sections array order)
const SECTIONS = {
  HERO: 0,
  PAIN: 1,
  EXPERTISE: 2,
  PROCESS: 3,
  PORTFOLIO: 4,
  WHY: 5,
  STATS: 6,
  TESTIMONIALS: 7,
  CTA: 8,
};

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const scrollContext = useContext(ScrollContext);
  const goToSection = scrollContext?.goToSection;

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      setIsAdmin(!!data);
    };

    checkAdminRole();
  }, [user]);

  const scrollToSection = (sectionIndex: number) => {
    if (goToSection) {
      goToSection(sectionIndex);
    }
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleLogoClick}
              className="text-2xl font-black tracking-wider hover:text-holographic transition-colors cursor-pointer"
            >
              <span className="text-foreground hover:text-holographic transition-colors">SHAPE</span>
            </button>
            <LanguageToggle />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-6 items-center">
            <button
              onClick={() => scrollToSection(SECTIONS.EXPERTISE)}
              className="text-foreground hover:text-accent transition-colors font-medium"
            >
              {t("nav.expertise")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.PROCESS)}
              className="text-foreground hover:text-accent transition-colors font-medium"
            >
              {t("nav.process")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.PORTFOLIO)}
              className="text-foreground hover:text-accent transition-colors font-medium"
            >
              {t("nav.portfolio")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.CTA)}
              className="text-foreground hover:text-accent transition-colors font-medium"
            >
              {t("nav.contact")}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    <Settings className="mr-2 h-4 w-4" />
                    {t("nav.accountDashboard")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=learning")}>
                    <GraduationCap className="mr-2 h-4 w-4" />
                    {t("nav.myCourses")}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      {t("nav.adminPanel")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => signOut()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate("/auth")}>
                {t("nav.login")}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pt-4 pb-2 flex flex-col gap-4">
            <div className="pb-2">
              <LanguageToggle />
            </div>
            <button
              onClick={() => scrollToSection(SECTIONS.EXPERTISE)}
              className="text-foreground hover:text-accent transition-colors font-medium text-left"
            >
              {t("nav.expertise")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.PROCESS)}
              className="text-foreground hover:text-accent transition-colors font-medium text-left"
            >
              {t("nav.process")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.PORTFOLIO)}
              className="text-foreground hover:text-accent transition-colors font-medium text-left"
            >
              {t("nav.portfolio")}
            </button>
            <button
              onClick={() => scrollToSection(SECTIONS.CTA)}
              className="text-foreground hover:text-accent transition-colors font-medium text-left"
            >
              {t("nav.contact")}
            </button>
            {user ? (
              <>
                <Button variant="ghost" onClick={() => navigate("/account")} className="justify-start">
                  <Settings className="mr-2 h-4 w-4" />
                  {t("nav.accountDashboard")}
                </Button>
                <Button variant="ghost" onClick={() => navigate("/account?tab=learning")} className="justify-start">
                  <GraduationCap className="mr-2 h-4 w-4" />
                  {t("nav.myCourses")}
                </Button>
                {isAdmin && (
                  <Button variant="ghost" onClick={() => navigate("/admin")} className="justify-start">
                    <Shield className="mr-2 h-4 w-4" />
                    {t("nav.adminPanel")}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => signOut()} className="justify-start">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("nav.signOut")}
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate("/auth")} className="w-full">
                {t("nav.login")}
              </Button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
